import {v4 as uuidv4} from 'uuid';
import {z} from 'zod';

import {TraceCallSchema} from '../../wfReactInterface/traceServerClientTypes';
import {ChatCompletion, ChatRequest, Message, Messages} from '../types';

export const GoogleGenAIFunctionCallSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string(),
    args: z.record(z.string(), z.any()).nullable().optional(),
  })
  .catchall(z.any());

export const GoogleGenAIFunctionResponseSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    response: z.any().optional(),
    // Some SDKs use "args" or other shapes for responses; accept loosely
  })
  .catchall(z.any());

export const GoogleGenAIPartSchema = z
  .object({
    text: z.string().nullable().optional(),
    functionCall: GoogleGenAIFunctionCallSchema.nullable().optional(),
    functionResponse: GoogleGenAIFunctionResponseSchema.nullable().optional(),
    inlineData: z.any().nullable().optional(),
    fileData: z.any().nullable().optional(),
  })
  .catchall(z.any());

export const GoogleGenAIContentSchema = z
  .object({
    role: z.string(),
    parts: z.array(GoogleGenAIPartSchema).default([]),
  })
  .catchall(z.any());

// Request variant A: Chat.send_message style
const GoogleGenAIRequestVariantChatSchema = z
  .object({
    self: z
      .object({
        __class__: z
          .object({
            module: z.string(),
            qualname: z.string().optional(),
            name: z.string().optional(),
          })
          .catchall(z.any()),
      })
      .catchall(z.any()),
    message: z.string(),
    model: z.string(),
  })
  .catchall(z.any());

// Request variant B: Models.generate_content style
const GoogleGenAIRequestVariantModelsSchema = z
  .object({
    model: z.string(),
    contents: z.union([z.string(), z.array(GoogleGenAIContentSchema)]),
    config: z
      .object({
        systemInstruction: z.string().nullable().optional(),
        temperature: z.number().nullable().optional(),
        seed: z.number().nullable().optional(),
        responseJsonSchema: z.any().nullable().optional(),
        responseMimeType: z.string().nullable().optional(),
        tools: z.any().nullable().optional(),
        toolConfig: z.any().nullable().optional(),
      })
      .catchall(z.any())
      .nullable()
      .optional(),
    self: z
      .object({
        __class__: z
          .object({
            module: z.string().optional(),
            qualname: z.string().optional(),
            name: z.string().optional(),
          })
          .catchall(z.any())
          .optional(),
        GoogleGenAIai: z.boolean().optional(),
      })
      .catchall(z.any())
      .nullable()
      .optional(),
  })
  .catchall(z.any());

export const GoogleGenAIRequestSchema = z.union([
  GoogleGenAIRequestVariantChatSchema,
  GoogleGenAIRequestVariantModelsSchema,
]);

// Response schemas
export const GoogleGenAICandidateSchema = z
  .object({
    content: GoogleGenAIContentSchema,
    finishReason: z.string().nullable().optional(),
    index: z.number().nullable().optional(),
  })
  .catchall(z.any());

export const GoogleGenAIResponseSchema = z
  .object({
    candidates: z.array(GoogleGenAICandidateSchema),
    responseId: z.string(),
    modelVersion: z.string(),
    createTime: z
      .object({
        val: z.string(),
      })
      .catchall(z.any()),
    usageMetadata: z
      .object({
        promptTokenCount: z.number().nullable().optional(),
        candidatesTokenCount: z.number().nullable().optional(),
        totalTokenCount: z.number().nullable().optional(),
      })
      .catchall(z.any())
      .nullable()
      .optional(),
  })
  .catchall(z.any());

export type GoogleGenAIRequest = z.infer<typeof GoogleGenAIRequestSchema>;
export type GoogleGenAIResponse = z.infer<typeof GoogleGenAIResponseSchema>;

export function isGoogleGenAIRequestFormat(
  input: unknown
): input is GoogleGenAIRequest {
  const result = GoogleGenAIRequestSchema.safeParse(input);
  return result.success;
}

export function isGoogleGenAICompletionFormat(
  input: unknown
): input is GoogleGenAIResponse {
  const result = GoogleGenAIResponseSchema.safeParse(input);
  return result.success;
}

export function isGoogleGenAITraceCall(
  input: unknown
): input is GoogleGenAIRequest {
  const result = GoogleGenAIRequestSchema.safeParse(input);
  return result.success;
}

export function isTraceCallChatFormatGoogleGenAI(
  call: TraceCallSchema
): boolean {
  return (
    isGoogleGenAIRequestFormat(call.inputs) &&
    isGoogleGenAICompletionFormat(call.output)
  );
}
type AnyObject = Record<string, any>;

function normalizeRole(role: string | undefined): string {
  return role === 'model' ? 'assistant' : (role ?? 'user');
}

function extractToolCalls(parts: any[]): {
  toolCalls: NonNullable<Message['tool_calls']>;
  remainingParts: any[];
} {
  const toolCalls: NonNullable<Message['tool_calls']> = [];
  const remainingParts: any[] = [];
  for (const part of parts ?? []) {
    if (part?.functionCall) {
      const name: string = part.functionCall.name;
      const argsObj: AnyObject | null | undefined = part.functionCall.args;
      let argsString: string;
      try {
        argsString = JSON.stringify(argsObj ?? {});
      } catch {
        argsString = '{}';
      }
      toolCalls.push({
        id: part.functionCall.id ?? uuidv4(),
        type: 'function',
        function: {name, arguments: argsString},
      });
      // Still keep the original part for content synthesis, but as internal marker
      remainingParts.push(part);
    } else {
      remainingParts.push(part);
    }
  }
  return {toolCalls, remainingParts};
}

function partsToMessageContent(parts: any[]): string | Message['content'] {
  const simpleTexts: string[] = [];
  const complexParts: NonNullable<Message['content']> = [];

  for (const part of parts ?? []) {
    if (part && typeof part.text === 'string' && part.text.length > 0) {
      simpleTexts.push(part.text);
      continue;
    }

    if (part && part.functionCall) {
      const name: string = part.functionCall.name;
      const argsObj: AnyObject | null | undefined = part.functionCall.args;
      const argsString = (() => {
        try {
          return JSON.stringify(argsObj ?? {});
        } catch {
          return '{}';
        }
      })();
      complexParts.push({
        type: 'tool_use',
        name,
        input: argsObj ?? {},
        content: argsString,
      });
      continue;
    }

    if (part && part.functionResponse) {
      // Represent as a tool_result style internal message
      const resp = part.functionResponse;
      complexParts.push({
        type: 'tool_result',
        name: typeof resp?.name === 'string' ? resp.name : undefined,
        content:
          typeof resp?.response === 'string'
            ? resp.response
            : JSON.stringify(resp?.response ?? {}),
      });
      continue;
    }
  }

  if (complexParts.length === 0) {
    return simpleTexts.join('\n');
  }

  if (simpleTexts.length > 0) {
    complexParts.unshift({type: 'text', text: simpleTexts.join('\n')});
  }
  return complexParts;
}

function GoogleGenAIContentsToMessages(contents: any[] | undefined): Messages {
  const messages: Messages = [];
  for (const c of contents ?? []) {
    const role = normalizeRole(c?.role);
    const parts = Array.isArray(c?.parts) ? c.parts : [];
    const {toolCalls, remainingParts} = extractToolCalls(parts);
    const content = partsToMessageContent(remainingParts);
    const message: Message = {role, content};
    if (toolCalls.length > 0) message.tool_calls = toolCalls;
    messages.push(message);
  }
  return messages;
}

export function normalizeGoogleGenAIChatRequest(input: unknown): ChatRequest {
  const parsed = GoogleGenAIRequestSchema.parse(input);

  // Variant A: Chat-like with a single `message`
  if (typeof (parsed as any).message === 'string') {
    const model: string = (parsed as any).model;
    const userMessage: string = (parsed as any).message;

    const messages: Messages = [{role: 'user', content: userMessage}];

    return {
      model,
      messages,
    };
  }

  // Variant B: Models.generate_content style
  const model: string = (parsed as any).model;
  const contents: any[] | string | undefined = (parsed as any).contents;
  const config: AnyObject | undefined = (parsed as any).config ?? undefined;

  // Handle contents as string or array
  const messages =
    typeof contents === 'string'
      ? [{role: 'user', content: contents}]
      : GoogleGenAIContentsToMessages(contents);

  // Prepend system instruction if provided
  if (
    typeof config?.systemInstruction === 'string' &&
    config.systemInstruction.trim().length > 0
  ) {
    messages.unshift({role: 'system', content: config.systemInstruction});
  }

  const chatRequest: ChatRequest = {
    model,
    messages,
  };

  // Optional fields mapping
  if (typeof config?.seed === 'number') chatRequest.seed = config.seed;
  if (typeof config?.temperature === 'number')
    chatRequest.temperature = config.temperature;

  // Pass response schema hint, if present
  if (config?.responseJsonSchema || config?.responseMimeType) {
    chatRequest.response_format = {
      responseJsonSchema: config?.responseJsonSchema ?? null,
      responseMimeType: config?.responseMimeType ?? null,
    } as AnyObject;
  }

  return chatRequest;
}

export function normalizeGoogleGenAIChatCompletion(
  input: unknown
): ChatCompletion {
  const parsed = GoogleGenAIResponseSchema.parse(input);

  const id = parsed.responseId;
  const model = parsed.modelVersion;
  const createdRaw = (parsed.createTime as any)?.val;
  const created = createdRaw
    ? Math.floor(new Date(createdRaw).getTime() / 1000)
    : Math.floor(Date.now() / 1000);

  const choices = (parsed.candidates ?? []).map((cand, idx) => {
    const role = normalizeRole(cand?.content?.role);
    const parts = Array.isArray((cand?.content as any)?.parts)
      ? (cand?.content as any).parts
      : [];
    const {toolCalls, remainingParts} = extractToolCalls(parts);
    const content = partsToMessageContent(remainingParts);

    const message: Message = {role, content};
    if (toolCalls.length > 0) message.tool_calls = toolCalls;
    return {
      index: typeof cand?.index === 'number' ? cand.index : idx,
      message,
      finish_reason: (cand as any)?.finishReason ?? 'stop',
    };
  });

  const usage = (parsed.usageMetadata ?? {}) as AnyObject;
  const prompt_tokens =
    typeof usage.promptTokenCount === 'number' ? usage.promptTokenCount : 0;
  const completion_tokens =
    typeof usage.candidatesTokenCount === 'number'
      ? usage.candidatesTokenCount
      : 0;
  const total_tokens =
    typeof usage.totalTokenCount === 'number'
      ? usage.totalTokenCount
      : prompt_tokens + completion_tokens;

  const chatCompletion: ChatCompletion = {
    id,
    model,
    created,
    system_fingerprint: '',
    choices,
    usage: {
      prompt_tokens,
      completion_tokens,
      total_tokens,
    },
  };

  return chatCompletion;
}
